<template>
  <div
    class="min-h-screen bg-background flex flex-col"
    style="padding-bottom: env(safe-area-inset-bottom);"
  >
    <!-- 標題區域 -->
    <div
      class="sticky top-0 bg-background z-10"
      style="padding-top: env(safe-area-inset-top);"
    >
      <div class="flex items-center px-6 pt-6 pb-4">
        <button v-if="store.babies.length > 0" @click="router.back()" class="p-2 -ml-2 text-textMuted">✕</button>
        <h2 class="flex-1 text-center font-bold text-lg text-textMain pr-6">新增寶寶資料</h2>
      </div>
    </div>

    <!-- 登入方式選擇對話框 - 一進入就顯示 -->
    <LoginWayDialog
      :show="showLoginWayDialog"
      @create-account="handleCreateAccount"
      @login="handleLogin"
      @skip="handleSkipToAnonymous"
    />

    <!-- 登入對話框 -->
    <LoginDialog
      :show="showLoginDialog"
      ref="loginDialogRef"
      @confirm="handleLoginConfirm"
      @cancel="closeLoginDialog"
    />

    <!-- 建立帳號對話框 -->
    <CreateAccountDialog
      :show="showCreateAccountDialog"
      ref="createAccountDialogRef"
      @confirm="handleCreateAccountConfirm"
      @cancel="closeCreateAccountDialog"
    />

    <!-- Loading 狀態 -->
    <div
      v-show="isLoading"
      class="flex-1 flex flex-col items-center justify-center"
    >
      <div class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
      <p class="text-textMuted">{{ loadingMessage }}</p>
    </div>

    <!-- 表單內容區 - 可滾動 -->
    <!-- 使用 v-show 替代 v-if，避免組件重新掛載導致表單狀態重置 -->
    <div
      v-show="canShowForm"
      class="flex-1 overflow-auto px-6"
      style="padding-bottom: calc(env(safe-area-inset-bottom) + 24px);"
      @click="handleFormClick"
    >
      <div class="bg-white rounded-3xl p-6 shadow-sm space-y-6 mb-6" @click.stop>
        <!-- Gender -->
        <div>
          <label class="block text-sm font-bold text-textMain mb-3">性別</label>
          <div class="flex gap-4">
            <div
              @click="formGender = 'boy'"
              class="flex-1 p-4 rounded-2xl border-2 flex flex-col items-center gap-2 cursor-pointer transition-all"
              :class="formGender === 'boy' ? 'border-blue-300 bg-blue-50 text-blue-500' : 'border-gray-100 text-gray-300'"
            >
              <span class="text-2xl">👦</span>
              <span class="text-sm font-bold">男寶</span>
            </div>
            <div
              @click="formGender = 'girl'"
              class="flex-1 p-4 rounded-2xl border-2 flex flex-col items-center gap-2 cursor-pointer transition-all"
              :class="formGender === 'girl' ? 'border-pink-300 bg-pink-50 text-pink-500' : 'border-gray-100 text-gray-300'"
            >
              <span class="text-2xl">👧</span>
              <span class="text-sm font-bold">女寶</span>
            </div>
          </div>
        </div>

        <!-- Name -->
        <div>
          <label class="block text-sm font-bold text-textMain mb-3">寶寶暱稱</label>
          <input
            ref="nicknameInput"
            :value="formNickname"
            @input="handleNicknameInput"
            @change="handleNicknameChange"
            @blur="handleNicknameBlur"
            type="text"
            inputmode="text"
            enterkeyhint="done"
            placeholder="例如：小饅頭"
            class="w-full bg-gray-50 p-4 rounded-xl outline-none focus:ring-2 focus:ring-primary/50 transition-all"
            @keydown.enter="handleNicknameEnter"
          >
        </div>

        <!-- Birthday -->
        <div>
          <label class="block text-sm font-bold text-textMain mb-3">出生日期</label>
          <MobileDatePicker
            v-model="formBirthDate"
            :max="todayDate"
          />
        </div>
      </div>

      <!-- 建立檔案按鈕：在表單下面（不置底、不 fixed） -->
      <button
        @click.prevent.stop="save"
        @touchend.prevent="save"
        :disabled="!isValid || isSaving"
        class="w-full bg-primary text-white font-bold py-4 rounded-2xl shadow-lg shadow-primary/30 disabled:opacity-50 disabled:shadow-none transition-all active:scale-[0.98]"
      >
        {{ isSaving ? '儲存中...' : '建立檔案' }}
      </button>
    </div>

    <!-- 成功/錯誤提示 -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-28 left-4 right-4 bg-red-600 text-white px-4 py-3 rounded-xl shadow-lg z-50 text-sm break-all"
      >
        {{ toast.message }}
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAppStore } from '../stores/useAppStore';
import { useAuthStore } from '../stores/useAuthStore';
import LoginWayDialog from '../components/LoginWayDialog.vue';
import LoginDialog from '../components/LoginDialog.vue';
import CreateAccountDialog from '../components/CreateAccountDialog.vue';
import MobileDatePicker from '../components/MobileDatePicker.vue';
import type { Baby, Gender } from '../types';

const router = useRouter();
const store = useAppStore();
const authStore = useAuthStore();

const showLoginWayDialog = ref(false);
const showLoginDialog = ref(false);
const showCreateAccountDialog = ref(false);
const canShowForm = ref(false); // 控制表單是否顯示
const isLoading = ref(false); // Loading 狀態
const loadingMessage = ref(''); // Loading 訊息

const loginDialogRef = ref<InstanceType<typeof LoginDialog> | null>(null);
const createAccountDialogRef = ref<InstanceType<typeof CreateAccountDialog> | null>(null);
const nicknameInput = ref<HTMLInputElement | null>(null);

// Toast 提示
const toast = reactive({
  show: false,
  message: ''
});

const showToast = (message: string, duration: number = 5000) => {
  toast.message = message;
  toast.show = true;
  setTimeout(() => {
    toast.show = false;
  }, duration);
};

// 今天的日期（用於限制不能選未來日期）
const todayDate = computed(() => {
  return new Date().toISOString().split('T')[0] || '';
});

// 表單資料 - 使用獨立的 ref 避免 reactive 重置問題
const formNickname = ref('');
const formGender = ref<Gender>('boy');
const formBirthDate = ref(new Date().toISOString().split('T')[0] || '');


// 表單驗證
const isValid = computed(() => {
  const valid = formNickname.value.trim() !== '' && formBirthDate.value !== '';
  return valid;
});

// 檢查並顯示登入對話框（必須在 watch 之前定義）
const checkAndShowLoginDialog = () => {
  // Auth 還沒 ready，等待
  if (!authStore.ready) {
    return;
  }

  // 如果已經有寶寶，直接顯示表單
  if (store.babies.length > 0) {
    canShowForm.value = true;
  } else if (!authStore.uid) {
    // 沒有用戶（首次使用），顯示登入方式選擇
    showLoginWayDialog.value = true;
  } else {
    // 已有用戶，直接顯示表單
    canShowForm.value = true;
  }
};

// 初始化：等待 auth ready 後，判斷是否顯示登入方式選擇
onMounted(() => {
  checkAndShowLoginDialog();
});

// 監聯 auth ready 狀態
watch(() => authStore.ready, (ready) => {
  if (ready) {
    checkAndShowLoginDialog();
  }
}, { immediate: true });

// 處理暱稱輸入框按下 Enter 鍵
const handleNicknameEnter = (event: KeyboardEvent) => {
  event.preventDefault();
  nicknameInput.value?.blur();
};

// 手動處理暱稱輸入（不依賴 v-model，解決 Android WebView Release build 問題）
const handleNicknameInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  formNickname.value = target.value;
};

const handleNicknameChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  formNickname.value = target.value;
};

const handleNicknameBlur = (event: Event) => {
  const target = event.target as HTMLInputElement;
  formNickname.value = target.value;
};

// 處理點擊表單外層空白處，收起鍵盤
const handleFormClick = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

// 儲存寶寶資料
const isSaving = ref(false);

const save = async () => {
  if (!isValid.value) {
    showToast('請填寫完整資料');
    return;
  }

  if (isSaving.value) {
    showToast('正在儲存中，請稍候');
    return;
  }

  isSaving.value = true;
  isLoading.value = true;
  loadingMessage.value = '正在建立寶寶檔案...';

  const baby: Baby = {
    id: `baby_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    nickname: formNickname.value.trim(),
    birthDate: formBirthDate.value,
    gender: formGender.value,
    createdAt: new Date().toISOString()
  };

  try {

    // 加入超時機制，避免永遠等待
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('操作超時（30秒），請檢查網路連接')), 30000);
    });

    await Promise.race([
      store.addBaby(baby),
      timeoutPromise
    ]);

    router.push('/timeline');
  } catch (error: any) {
    const errorMsg = error?.message || '保存失敗，請稍後再試';
    showToast(errorMsg);
  } finally {
    isSaving.value = false;
    isLoading.value = false;
  }
};

// 處理「建立帳號」
const handleCreateAccount = () => {
  showLoginWayDialog.value = false;
  showCreateAccountDialog.value = true;
};

// 處理「登入帳號」
const handleLogin = () => {
  showLoginWayDialog.value = false;
  showLoginDialog.value = true;
};

// 處理「維持匿名使用」
const handleSkipToAnonymous = async () => {
  showLoginWayDialog.value = false;
  isLoading.value = true;
  loadingMessage.value = '正在建立帳號...';

  try {
    await authStore.createAnonymousAccount();
    isLoading.value = false;
    canShowForm.value = true;
  } catch (error: any) {
    console.error('建立匿名帳號失敗:', error);
    isLoading.value = false;
    const errorMsg = error?.code
      ? `${error.code}: ${error.message}`
      : (error.message || '建立匿名帳號失敗');
    showToast(errorMsg);
    showLoginWayDialog.value = true;
  }
};

// 處理登入確認
const handleLoginConfirm = async (email: string, password: string) => {
  try {
    // 登入
    await authStore.signInWithEmail(email, password);

    // 重新載入該帳號的資料
    await store.initFromCloud();

    loginDialogRef.value?.resetForm();
    showLoginDialog.value = false;

    showToast('登入成功！');

    // 如果該帳號已有寶寶，跳轉到 Timeline
    if (store.babies.length > 0) {
      setTimeout(() => {
        router.push('/timeline');
      }, 500);
    } else {
      // 如果沒有寶寶，顯示表單讓用戶新增
      canShowForm.value = true;
    }
  } catch (error: any) {
    loginDialogRef.value?.setError(error.message || '登入失敗，請稍後再試');
    loginDialogRef.value?.setLoading(false);
  }
};

// 關閉登入對話框（用戶取消登入）
const closeLoginDialog = () => {
  showLoginDialog.value = false;
  // 用戶取消登入，回到登入方式選擇
  showLoginWayDialog.value = true;
};

// 處理建立帳號確認（直接建立正式帳號，不經過匿名）
const handleCreateAccountConfirm = async (email: string, password: string) => {
  try {
    // 直接建立正式帳號
    await authStore.createEmailAccount(email, password);

    createAccountDialogRef.value?.resetForm();
    closeCreateAccountDialog();
    canShowForm.value = true;
    showToast('帳號建立成功！');
  } catch (error: any) {
    createAccountDialogRef.value?.setError(error.message || '建立失敗，請稍後再試');
    createAccountDialogRef.value?.setLoading(false);
  }
};

// 關閉建立帳號對話框
const closeCreateAccountDialog = () => {
  showCreateAccountDialog.value = false;
  // 如果關閉建立帳號對話框，顯示表單（讓用戶可以繼續新增寶寶）
  canShowForm.value = true;
};
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}
</style>